import { useCallback, useEffect, useState } from 'react';
import { api, type Informe } from '../api';
import { etiquetaDeCanal } from '../canales';
import { milisegundos } from '../releases';
import { Crash } from './Crash';
import { Enlaces } from './Enlaces';
import { Tabla } from './Tabla';

/**
 * Informe de análisis asistido.
 *
 * El resto del panel exige saber de antemano qué mirar: qué proceso, qué
 * canal, qué ventana. Quien recién abre la herramienta no lo sabe, y quien la
 * usa todos los días tampoco a las tres de la mañana. Esta pantalla recorre lo
 * que hay y lo ordena.
 *
 * Cada hallazgo se muestra con las cuatro cosas separadas —qué se observa,
 * dónde aplica, dónde está la causa, qué hacer— y no como un párrafo. Un
 * párrafo obliga a leerlo entero para saber si sirve; cuatro bloques dejan
 * saltar al que importa.
 */

const CLASES_SEVERIDAD: Record<string, string> = { alta: 'alta', media: 'media', baja: 'baja' };

const ETIQUETA_ORIGEN: Record<string, string> = {
  anomalia: 'Cambió',
  cronico: 'Viene así',
  vital: 'Percibido',
};

const ETIQUETA_ALCANCE: Record<string, string> = {
  transversal: 'Todos los canales',
  un_canal: 'Un solo canal',
  sin_contraste: 'Sin contraste',
};

/**
 * Enlaces de descarga en vez de fetch + Blob.
 *
 * El navegador manda la cookie de sesión con la navegación, el portal responde
 * con `Content-Disposition: attachment` y el archivo baja con su nombre. Armar
 * el documento en el cliente exigiría duplicar el renderizado y, sobre todo,
 * dejaría la exportación fuera de la bitácora: sacar datos de un cliente del
 * sistema es justamente lo que hay que dejar anotado.
 */
function Exportar({ desde }: { desde: string }) {
  const parametros = `from=${encodeURIComponent(desde)}&to=${encodeURIComponent(new Date().toISOString())}`;
  return (
    <div className="exportar">
      <a href={`/informe/analisis.html?${parametros}`} download>
        Descargar HTML
      </a>
      <a href={`/informe/analisis.md?${parametros}`} download>
        Descargar Markdown
      </a>
      <span className="tenue">El HTML se imprime a PDF desde el navegador.</span>
    </div>
  );
}

export function Analisis({ desde, onError }: { desde: string; onError: (err: unknown) => void }) {
  const [informe, setInforme] = useState<Informe | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setInforme(await api.analisis(desde));
    } catch (err) {
      onError(err);
    } finally {
      setCargando(false);
    }
  }, [desde, onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (cargando && !informe) return <p className="vacio">Analizando…</p>;
  if (!informe) return null;

  return (
    <section className="analisis">
      <div className="analisis-cabecera">
        <h3>Salud general</h3>
        <Exportar desde={desde} />
      </div>
      <Tabla className="salud">
        <thead>
          <tr>
            <th>Canal</th>
            <th className="num">Ejecuciones</th>
            <th className="num">Error</th>
            <th className="num" title="El servicio evaluó y respondió que no (BUSINESS_ERROR)">
              De negocio
            </th>
            <th className="num">Abandono</th>
            <th className="num">p50</th>
            <th className="num">p95</th>
            <th className="num">p99</th>
          </tr>
        </thead>
        <tbody>
          {informe.salud.map((s) => (
            <tr key={s.canal}>
              <td>{etiquetaDeCanal(s.canal)}</td>
              <td className="num">{s.ejecuciones}</td>
              <td className="num">{s.tasa_error}%</td>
              <td className="num">{s.tasa_negocio === undefined ? '—' : `${s.tasa_negocio}%`}</td>
              <td className="num">{s.tasa_abandono}%</td>
              <td className="num">{milisegundos(s.p50_ms)}</td>
              <td className="num">{milisegundos(s.p95_ms)}</td>
              <td className="num">{milisegundos(s.p99_ms)}</td>
            </tr>
          ))}
        </tbody>
      </Tabla>

      <h3 className="con-espacio">
        {informe.hallazgos.length === 0
          ? 'Sin hallazgos'
          : `${informe.hallazgos.length} ${informe.hallazgos.length === 1 ? 'hallazgo' : 'hallazgos'}`}
      </h3>

      {informe.hallazgos.length === 0 && (
        <p className="vacio">
          Nada supera los umbrales en esta ventana: ni cambios respecto de la línea base o del
          despliegue anterior, ni tasas por encima de lo aceptable. Mira las limitaciones de más
          abajo antes de leer esto como que no hay problemas.
        </p>
      )}

      {informe.hallazgos.map((h) => (
        <article key={h.id} className={`hallazgo severidad-${CLASES_SEVERIDAD[h.severidad]}`}>
          <header>
            <h4>{h.titulo}</h4>
            <span className="etiquetas">
              <span className={`chip sev-${h.severidad}`}>{h.severidad}</span>
              <span className="chip">{ETIQUETA_ORIGEN[h.origen] ?? h.origen}</span>
              <span className="chip">{ETIQUETA_ALCANCE[h.alcance.clase] ?? h.alcance.clase}</span>
            </span>
          </header>

          <dl>
            <dt>Qué se observa</dt>
            <dd>{h.sintoma}</dd>

            <dt>Dónde aplica</dt>
            <dd>{h.alcance.conclusion}</dd>

            {h.causaRaiz && (
              <>
                <dt>Dónde está la causa</dt>
                <dd>
                  {h.causaRaiz.conclusion}
                  {h.causaRaiz.atribucion.length > 0 && (
                    <table className="atribucion">
                      <tbody>
                        {h.causaRaiz.atribucion.slice(0, 5).map((a) => (
                          <tr key={`${a.servicio}|${a.operacion}`}>
                            <td className="barra">
                              <span style={{ width: `${Math.min(a.pct, 100)}%` }} />
                            </td>
                            <td className="num">{a.pct.toFixed(0)}%</td>
                            <td>
                              <code>{a.operacion}</code>{' '}
                              <span className="tenue">{a.servicio}</span>
                            </td>
                            <td className="num tenue">{milisegundos(a.propio_ms)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </dd>
              </>
            )}

            <dt>Qué hacer</dt>
            <dd>
              <ol className="acciones">
                {h.acciones.map((a) => (
                  <li key={a.texto}>
                    {a.texto}
                    <span className="porque">{a.porque}</span>
                  </li>
                ))}
              </ol>
            </dd>
          </dl>

          {h.evidencia.trazas.length > 0 && h.origen === 'crash' && (
            // Un crash trae una traza de ejemplo y todas las del grupo tienen
            // el mismo volcado: alcanza con resolver una.
            <footer>
              <Crash traceId={h.evidencia.trazas[0] as string} />
            </footer>
          )}

          {h.evidencia.trazas.length > 0 && h.origen !== 'crash' && (
            <footer className="tenue">
              Ejecuciones de muestra:{' '}
              {h.evidencia.trazas.map((t) => (
                <code key={t}>{t.slice(0, 12)}</code>
              ))}
            </footer>
          )}
        </article>
      ))}

      <Enlaces desde={desde} onError={onError} />

      <h3 className="con-espacio">Qué no mira este informe</h3>
      <ul className="limitaciones">
        {informe.limitaciones.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </section>
  );
}
